/* eslint-disable no-console */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import PageBreadcrumb from '../../Components/Common/PageBreadcrumb';
import DataTable from 'react-data-table-component';
import ActionButton from '../../Components/Common/ActionButton';
import DeleteModal from '../../Components/Common/DeleteModal';
import ListPager from '../../Components/Common/ListPager';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListFilterSelect,
  ListSearch,
} from '../../Components/Common/ListFilters';
import { EmptyState, SkeletonRows } from '../../Components/Common/ListStates';
import notify, { MESSAGES } from '../../utils/notify';
import '../TenderOpening/components/TenderOpening.css';
import axios from 'axios';
import { api } from '../../config';
import { getAuthToken } from '../../auth/tokenvalidation';
import {
  FormField,
  FormGrid,
  SelectField,
  TextField,
} from '../../Components/Form';
import {
  BtnBold,
  BtnItalic,
  Editor,
  EditorProvider,
  Toolbar,
  BtnBulletList,
  BtnClearFormatting,
  BtnLink,
  BtnNumberedList,
  BtnRedo,
  BtnStrikeThrough,
  BtnStyles,
  BtnUnderline,
  BtnUndo,
  Separator,
} from 'react-simple-wysiwyg';

const DocumentTemplateView = () => {
  const token = getAuthToken();

  const [data, setData] = useState({ items: [] });
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [modal, setModal] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalRole, setModalRole] = useState(false);
  const [roleFormData, setRoleFormData] = useState({
    name: null,
    template: null,
    clausaType: null,
    id: null,
  });

  const [clauseTypeData, setClauseTypeData] = useState({ items: [] });
  const [filterText, setFilterText] = useState('');
  const [search, setSearch] = useState('');
  const requestRef = useRef(0);
  const [clauseFilter, setClauseFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  const handleDeleteModal = id => {
    const selectedRow = data.items.find(item => item.id === id);
    setInvoice(selectedRow);
    setDeleteModal(true);
  };

  const handleDeleteInvoice = async () => {
    if (invoice) {
      try {
        const response = await fetch(
          `${api.API_URL}/template-procurement-document/${invoice.id}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || `HTTP error! status: ${response.status}`
          );
        }

        notify.success('Document Template deleted successfully.');
        setDeleteModal(false);
        setInvoice(null);
        fetchData(currentPage, rowsPerPage, token, search, clauseFilter);
      } catch (err) {
        notify.apiError(err, 'delete role');
      }
    }
  };

  const deleteMultiple = () => {
    // Implement multiple delete functionality
    // This would typically involve deleting selected items
    // For now, just close the modal
    setDeleteModalMulti(false);
  };

  const toggleRoleModal = () => {
    setModal(!modal);
  };

  const columns = [
    {
      id: 'name',
      name: 'Name',
      selector: row => row.name,
      sortable: true,
      width: '40%',
      cell: row => (
        <span className='to-title' title={String(row.name ?? '')}>
          {row.name ?? '-'}
        </span>
      ),
    },

    {
      id: 'clause',
      name: 'Clause',
      selector: row => row.clauseDetail?.clauseTitle,
      sortable: true,
      width: '45%',
      cell: row => (
        <span title={String(row.clauseDetail?.clauseTitle ?? '')}>
          {row.clauseDetail?.clauseTitle || '-'}
        </span>
      ),
    },

    {
      name: <span className='text-center w-100'>Action</span>,
      width: '15%',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-edit-line'
            label='Edit'
            onClick={() => handleUpdateModal(row.id)}
          />
          <ActionButton
            variant='icon'
            size='sm'
            danger
            icon='ri-delete-bin-line'
            label='Delete'
            confirm={false}
            onClick={() => handleDeleteModal(row.id)}
          />
        </div>
      ),
    },
  ];

  document.title = 'Data Document Template';

  const handleChange = e => {
    const { name, value } = e.target;
    setRoleFormData({ ...roleFormData, [name]: value });
  };

  const handleNoteTextChange = (event, field) => {
    setRoleFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  // Debounce typing so each keystroke is not a request.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(filterText.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [filterText]);

  useEffect(() => {
    fetchClauseType();
  }, [token]);

  useEffect(() => {
    fetchData(currentPage, rowsPerPage, token, search, clauseFilter);
  }, [currentPage, rowsPerPage, token, search, clauseFilter]);

  const handleClauseFilterChange = value => {
    setClauseFilter(value);
    setCurrentPage(1);
  };

  const handleFilterChange = e => {
    setFilterText(e.target.value);
    // Clearing applies at once; typing waits for the debounce.
    if (!e.target.value) {
      setSearch('');
      setCurrentPage(1);
    }
  };

  const clearFilter = () => {
    setFilterText('');
    setSearch('');
    setCurrentPage(1);
    setClauseFilter('');
  };

  // Clause options reuse the clause list this page already loads for the form.
  const clauseFilterOptions = [...clauseTypeData.items].sort((a, b) =>
    String(a.label ?? '').localeCompare(String(b.label ?? ''))
  );

  const filterChips = [
    filterText && {
      key: 'search',
      label: 'Search',
      value: filterText,
      onRemove: () => handleFilterChange({ target: { value: '' } }),
    },
    clauseFilter && {
      key: 'clause',
      label: 'Clause',
      value:
        clauseFilterOptions.find(o => o.value === clauseFilter)?.label ??
        clauseFilter,
      onRemove: () => handleClauseFilterChange(''),
    },
  ].filter(Boolean);
  const hasFilters = filterChips.length > 0;

  const fetchData = async (
    currentPage,
    rowsPerPage,
    token,
    search,
    clauseDetailId
  ) => {
    const reqId = ++requestRef.current;
    setLoading(true);
    fetch(
      `${api.API_URL}/template-procurement-document?PageNumber=${currentPage}&PageSize=${rowsPerPage}${search ? `&Search=${encodeURIComponent(search)}` : ''}${clauseDetailId ? `&ClauseDetailId=${clauseDetailId}` : ''}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      }
    )
      .then(data => data.json())
      .then(
        result => {
          if (reqId !== requestRef.current) return; // stale response
          setData({
            items: result.data.items || [],
          });
          setTotalRows(result.data.totalItems || 0);
        },
        _error => {
          // Handle error appropriately
        }
      )
      .catch(_error => {
        // Handle error silently or show appropriate message
      })
      .finally(() => {
        if (reqId === requestRef.current) setLoading(false);
      });
  };

  const fetchClauseType = async () => {
    try {
      const response = await fetch(
        `${api.API_URL}/clause-detail?PageNumber=1&PageSize=100`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result = await response.json();
      const items = (result.data?.items || []).map(item => ({
        value: item.id,
        label: item.clauseTitle,
      }));
      setClauseTypeData({ items });
    } catch (error) {
      console.error('Error fetching clause type:', error);
    }
  };

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const handleNewModal = () => {
    setRoleFormData({
      name: null,
      template: null,
      clausaType: null,
      id: null,
    });
    roleModal();
  };

  const handleUpdateModal = async (mid = null) => {
    await fetchRoleDetail(mid);
    roleModal();
  };

  const roleModal = () => {
    setModalRole(!modalRole);
  };

  const selectedFieldsRole = {
    name: roleFormData.name,
    template: roleFormData.template,
    clauseDetailId: roleFormData.clausaType,
  };

  const handleAddDataRole = async () => {
    try {
      await axios.post(
        `${api.API_URL}/template-procurement-document`,
        selectedFieldsRole,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notify.success('Document Template added successfully.');
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      fetchData(currentPage, rowsPerPage, token, search, clauseFilter);
    }
  };

  const fetchRoleDetail = async mid => {
    try {
      const response = await axios.get(
        `${api.API_URL}/template-procurement-document/${mid}`,
        {
          withXSRFToken: true,
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: `application/json`,
          },
        }
      );
      const data = response?.data?.data ?? response?.data ?? response;
      setRoleFormData({
        name: data.name || null,
        template: data.template || null,
        clausaType: data.clauseDetailId || null,
        id: data.id || null,
      });
    } catch (error) {
      // Handle error silently or show appropriate message
    }
  };

  const handleUpdDataRole = async mid => {
    try {
      await axios.put(
        `${api.API_URL}/template-procurement-document/${mid}`,
        selectedFieldsRole,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notify.success(MESSAGES.updated);
      console.log('hasil', selectedFieldsRole);
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      fetchData(currentPage, rowsPerPage, token, search, clauseFilter);
    }
  };

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={() => handleDeleteInvoice()}
          onCloseClick={() => setDeleteModal(false)}
          title='Delete this document template?'
          message='It will be permanently deleted. This cannot be undone.'
          subject={invoice?.name}
          confirmLabel='Delete document template'
        />
        <DeleteModal
          show={deleteModalMulti}
          onDeleteClick={() => {
            deleteMultiple();
            setDeleteModalMulti(false);
          }}
          onCloseClick={() => setDeleteModalMulti(false)}
        />
        <Container fluid>
          <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4'>
                <div className='to-hero mb-4'>
                  <PageBreadcrumb
                    fallback={{
                      label: 'Document Template',
                      to: '/apps-document-template/view',
                    }}
                  />

                  <div className='d-flex align-items-center gap-3 flex-wrap'>
                    <span className='to-hero-icon'>
                      <i className='ri-file-text-line' aria-hidden='true' />
                    </span>
                    <div className='flex-grow-1'>
                      <h4 className='to-hero-title'>Document Template</h4>
                      <p className='to-hero-subtitle'>
                        Maintain the procurement document templates and their
                        clauses.
                      </p>
                    </div>
                    <div className='d-flex gap-2 flex-wrap'>
                      <button
                        type='button'
                        className='to-hero-btn'
                        onClick={() =>
                          fetchData(
                            currentPage,
                            rowsPerPage,
                            token,
                            search,
                            clauseFilter
                          )
                        }
                      >
                        <i className='ri-refresh-line' aria-hidden='true' />
                        Refresh
                      </button>
                      <button
                        type='button'
                        className='to-hero-btn to-hero-btn--solid'
                        onClick={() => handleNewModal()}
                      >
                        <i className='ri-add-line' aria-hidden='true' />
                        Create New Document Template
                      </button>
                    </div>
                  </div>
                </div>

                <div className='to-panel'>
                  <div className='to-toolbar'>
                    <ListSearch
                      value={filterText}
                      onChange={handleFilterChange}
                      placeholder='Search document templates...'
                      ariaLabel='Search document templates'
                    />
                    <ListFilterSelect
                      label='Clause'
                      value={clauseFilter}
                      onChange={handleClauseFilterChange}
                      allLabel='All Clauses'
                      options={clauseFilterOptions}
                      ariaLabel='Filter by clause'
                    />
                    <ClearFiltersButton
                      count={filterChips.length}
                      onClick={clearFilter}
                    />
                    <span className='to-toolbar-meta'>
                      {loading
                        ? 'Loading...'
                        : `Showing ${startEntry}–${endEntry} of ${totalRows} document templates`}
                    </span>
                  </div>

                  <ActiveFilterChips chips={filterChips} />

                  <div className='to-table-wrap'>
                    <DataTable
                      columns={columns}
                      data={data.items}
                      highlightOnHover
                      persistTableHead
                      defaultSortFieldId='name'
                      defaultSortAsc
                      progressPending={loading}
                      progressComponent={<SkeletonRows />}
                      noDataComponent={
                        <EmptyState
                          title='No document template found'
                          emptyHint='No document templates have been created yet. Templates added here become available for generating documents.'
                          filteredHint='No document template matches the current filters.'
                          hasFilters={hasFilters}
                          onClear={clearFilter}
                          action={{
                            label: 'Create New Document Template',
                            onClick: handleNewModal,
                          }}
                        />
                      }
                      customStyles={{
                        headCells: {
                          style: { paddingLeft: '12px', paddingRight: '12px' },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                          },
                        },
                        table: { style: { width: '100%' } },
                      }}
                    />
                  </div>

                  <ListPager
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={totalRows}
                    isLoading={loading}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handlePerRowsChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <Modal
            isOpen={modalRole}
            role='dialog'
            autoFocus={true}
            centered
            id='addAddressModal'
            toggle={toggleRoleModal}
            data-bs-backdrop='static'
            size='xl'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => {
                setModalRole(!modalRole);
              }}
            >
              <span className='modal-title text-info fw-bold'>
                {roleFormData.id !== null
                  ? 'Edit Document Template'
                  : 'Add Document Template'}
              </span>
            </ModalHeader>

            <ModalBody>
              <FormGrid columns={1}>
                <TextField
                  label='Document template name'
                  name='name'
                  placeholder='e.g. Tender Invitation Letter'
                  value={roleFormData.name}
                  onChange={handleChange}
                />
                <SelectField
                  label='Clause type'
                  name='clausaType'
                  options={clauseTypeData.items}
                  value={roleFormData.clausaType}
                  onChange={value =>
                    setRoleFormData(prev => ({
                      ...prev,
                      clausaType: value ?? null,
                    }))
                  }
                />
                <FormField label='Template' name='template' group>
                  {() => (
                    <div className='snow-editor'>
                      <EditorProvider>
                        <Editor
                          id='template'
                          name='template'
                          value={roleFormData.template || ''}
                          onChange={e => handleNoteTextChange(e, 'template')}
                          style={{
                            height: '500px',
                            maxHeight: '60vh',
                            overflowY: 'auto',
                            whiteSpace: 'normal',
                            overflowWrap: 'break-word',
                            wordBreak: 'break-word',
                          }}
                          className='form-control'
                        >
                          <Toolbar>
                            <BtnBold />
                            <BtnItalic />
                            <BtnBulletList />
                            <BtnClearFormatting />
                            <BtnLink />
                            <BtnNumberedList />
                            <BtnRedo />
                            <BtnStrikeThrough />
                            <BtnStyles />
                            <BtnUnderline />
                            <BtnUndo />
                            <Separator />
                          </Toolbar>
                        </Editor>
                      </EditorProvider>
                    </div>
                  )}
                </FormField>
              </FormGrid>
            </ModalBody>

            <ModalFooter>
              <ActionButton
                variant='secondary'
                size='sm'
                icon='ri-close-line'
                onClick={() => {
                  setModalRole(!modalRole);
                }}
              >
                Close
              </ActionButton>
              <ActionButton
                variant='primary'
                size='sm'
                icon='ri-save-line'
                onClick={async () => {
                  await (roleFormData.id !== null
                    ? handleUpdDataRole(roleFormData.id)
                    : handleAddDataRole());
                  setModalRole(false);
                }}
              >
                {roleFormData.id !== null ? 'Update' : 'Save'}
              </ActionButton>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DocumentTemplateView;
